import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import api from '../services/api';
import { LangContext } from '../App';

const T = {
  fr: {
    title: 'Bibliothèque de recettes',
    sousTitre: 'Recettes de gibier sauvage et de poisson',
    recherche: 'Rechercher une recette…',
    filtres: 'Filtres',
    species: 'Espèce',
    difficulty: 'Difficulté',
    toutesEspeces: 'Toutes les espèces',
    toutesDifficultes: 'Toutes les difficultés',
    soumettre: 'Soumettre une recette',
    aucune: 'Aucune recette trouvée',
    editorial: 'Éditorial',
    avis: 'avis',
    favoris: 'favoris',
    prep: 'Prép.',
    cuisson: 'Cuisson',
    min: 'min',
    servings: 'servings',
    charger: 'Charger plus',
    especes: [
      'orignal',
      'chevreuil',
      'caribou',
      'dinde_sauvage',
      'outarde',
      'canard',
      'bernache',
      'brochet',
      'dore',
      'achigan',
      'truite',
      'saumon',
      'autre',
    ],
    especesLabels: {
      orignal: 'Orignal',
      chevreuil: 'Chevreuil',
      caribou: 'Caribou',
      dinde_sauvage: 'Dinde sauvage',
      outarde: 'Outarde',
      canard: 'Canard',
      bernache: 'Bernache',
      brochet: 'Brochet',
      dore: 'Doré',
      achigan: 'Achigan',
      truite: 'Truite',
      saumon: 'Saumon',
      autre: 'Autre',
    },
    difficultes: [
      { v: 'facile', l: 'Facile' },
      { v: 'moyen', l: 'Moyen' },
      { v: 'difficile', l: 'Difficile' },
      { v: 'expert', l: 'Expert' },
    ],
  },
  en: {
    title: 'Recipe Library',
    sousTitre: 'Wild game and fish recipes',
    recherche: 'Search a recipe…',
    filtres: 'Filters',
    species: 'Species',
    difficulty: 'Difficulty',
    toutesEspeces: 'All species',
    toutesDifficultes: 'All difficulties',
    soumettre: 'Submit a recipe',
    aucune: 'No recipes found',
    editorial: 'Editorial',
    avis: 'reviews',
    favoris: 'favourites',
    prep: 'Prep',
    cuisson: 'Cook',
    min: 'min',
    servings: 'servings',
    charger: 'Load more',
    especes: [
      'orignal',
      'chevreuil',
      'caribou',
      'dinde_sauvage',
      'outarde',
      'canard',
      'bernache',
      'brochet',
      'dore',
      'achigan',
      'truite',
      'saumon',
      'autre',
    ],
    especesLabels: {
      orignal: 'Moose',
      chevreuil: 'Deer',
      caribou: 'Caribou',
      dinde_sauvage: 'Wild Turkey',
      outarde: 'Canada Goose',
      canard: 'Duck',
      bernache: 'Brant',
      brochet: 'Pike',
      dore: 'Walleye',
      achigan: 'Bass',
      truite: 'Trout',
      saumon: 'Salmon',
      autre: 'Other',
    },
    difficultes: [
      { v: 'facile', l: 'Easy' },
      { v: 'moyen', l: 'Medium' },
      { v: 'difficile', l: 'Hard' },
      { v: 'expert', l: 'Expert' },
    ],
  },
};

const DIFFICULTY_COLOR = {
  facile: '#34d399',
  moyen: '#fbbf24',
  difficile: '#fb923c',
  expert: '#fb7185',
};

export default function Recettes() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [recipes, setRecettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [species, setEspece] = useState('');
  const [difficulty, setDifficulte] = useState('');
  const [q, setQ] = useState('');
  const [filtreOuvert, setFiltreOuvert] = useState(false);
  const filtreRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filtreRef.current && !filtreRef.current.contains(e.target)) {
        setFiltreOuvert(false);
      }
    };
    if (filtreOuvert) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtreOuvert]);

  const charger = async (p = 1, reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limite: 12 });
      if (species) params.set('species', species);
      if (difficulty) params.set('difficulty', difficulty);
      if (q) params.set('q', q);
      const { data } = await api.get(`/recipes?${params}`);
      const rows = data.data || [];
      setRecettes((prev) => (reset || p === 1 ? rows : [...prev, ...rows]));
      setPage(p);
      setHasMore(p < (data.pages || 1));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    charger(1, true);
  }, [species, difficulty]);

  const handleSearch = (e) => {
    e.preventDefault();
    charger(1, true);
  };

  const s = {
    page: { minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-texte)' },
    hero: {
      background: 'linear-gradient(135deg, #0a1f0e 0%, #1f3e1f 100%)',
      padding: '3rem 2rem 2.5rem',
      textAlign: 'center',
    },
    heroTitle: {
      fontFamily: 'Georgia, serif',
      fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
      color: '#fff',
      margin: '0 0 0.5rem',
    },
    heroSub: { color: 'rgba(255,255,255,0.65)', fontSize: '1rem', margin: 0 },
    toolbar: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1.2rem 2rem',
      background: 'rgba(0,0,0,0.3)',
      flexWrap: 'wrap',
    },
    searchForm: { display: 'flex', gap: '8px', flex: 1, minWidth: '200px' },
    searchInput: {
      flex: 1,
      padding: '9px 14px',
      borderRadius: '8px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.9rem',
      outline: 'none',
    },
    btnSearch: {
      padding: '9px 16px',
      borderRadius: '8px',
      border: 'none',
      background: 'var(--couleur-vert-accent)',
      color: '#fff',
      cursor: 'pointer',
      fontSize: '0.9rem',
    },
    btnFiltres: {
      padding: '9px 14px',
      borderRadius: '8px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      cursor: 'pointer',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    btnSoumettre: {
      padding: '9px 16px',
      borderRadius: '8px',
      border: 'none',
      background: 'var(--couleur-or)',
      color: '#0a1f0e',
      fontWeight: 700,
      cursor: 'pointer',
      fontSize: '0.9rem',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
    },
    filtrePanel: {
      position: 'absolute',
      top: '100%',
      right: 0,
      zIndex: 100,
      background: '#1a2e1a',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '10px',
      padding: '16px',
      minWidth: '220px',
      marginTop: '6px',
    },
    select: {
      width: '100%',
      padding: '8px 10px',
      borderRadius: '6px',
      background: 'rgba(255,255,255,0.08)',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#fff',
      fontSize: '0.85rem',
      cursor: 'pointer',
      marginBottom: '10px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px',
      padding: '2rem',
    },
    card: {
      background: 'var(--couleur-fond-carte)',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.08)',
      overflow: 'hidden',
      transition: 'transform 0.15s, box-shadow 0.15s',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
      display: 'block',
    },
    cardImg: {
      width: '100%',
      height: '180px',
      background: 'linear-gradient(135deg,#0a1f0e,#1f4020)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
    },
    cardBody: { padding: '1rem' },
    cardTitle: { fontWeight: 600, fontSize: '1rem', margin: '0 0 0.4rem', color: '#fff' },
    cardMeta: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '0.6rem' },
    badge: (color) => ({
      fontSize: '0.72rem',
      padding: '2px 8px',
      borderRadius: '999px',
      background: `${color}22`,
      color,
      border: `1px solid ${color}44`,
      fontWeight: 600,
    }),
    cardStats: { display: 'flex', gap: '12px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' },
  };

  const especeEmoji = {
    orignal: '🦌',
    chevreuil: '🦌',
    caribou: '🦌',
    dinde_sauvage: '🦃',
    outarde: '🪿',
    canard: '🦆',
    bernache: '🪿',
    brochet: '🐟',
    dore: '🐟',
    achigan: '🐟',
    truite: '🐟',
    saumon: '🐟',
  };

  return (
    <div style={s.page}>
      <Header />
      <div style={s.hero}>
        <h1 style={s.heroTitle}>{t.title}</h1>
        <p style={s.heroSub}>{t.sousTitre}</p>
      </div>

      <div style={s.toolbar}>
        <form style={s.searchForm} onSubmit={handleSearch}>
          <input
            style={s.searchInput}
            placeholder={t.recherche}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" style={s.btnSearch}>
            🔍
          </button>
        </form>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }} ref={filtreRef}>
            <button style={s.btnFiltres} onClick={() => setFiltreOuvert((o) => !o)}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <circle cx="8" cy="6" r="2" fill="currentColor" stroke="none" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none" />
                <line x1="4" y1="18" x2="20" y2="18" />
                <circle cx="10" cy="18" r="2" fill="currentColor" stroke="none" />
              </svg>
              {t.filtres}
            </button>
            {filtreOuvert && (
              <div style={s.filtrePanel}>
                <label
                  style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.5)',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  {t.species}
                </label>
                <select
                  style={s.select}
                  value={species}
                  onChange={(e) => {
                    setEspece(e.target.value);
                    setFiltreOuvert(false);
                  }}
                >
                  <option value="">{t.toutesEspeces}</option>
                  {t.especes.map((e) => (
                    <option key={e} value={e}>
                      {t.especesLabels[e]}
                    </option>
                  ))}
                </select>
                <label
                  style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.5)',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  {t.difficulty}
                </label>
                <select
                  style={s.select}
                  value={difficulty}
                  onChange={(e) => {
                    setDifficulte(e.target.value);
                    setFiltreOuvert(false);
                  }}
                >
                  <option value="">{t.toutesDifficultes}</option>
                  {t.difficultes.map((d) => (
                    <option key={d.v} value={d.v}>
                      {d.l}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <Link to="/submit-recipe" style={s.btnSoumettre}>
            + {t.soumettre}
          </Link>
        </div>
      </div>

      {loading && recipes.length === 0 ? (
        <div style={s.grid}>
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <div key={i} style={{ ...s.card, opacity: 0.4, pointerEvents: 'none' }}>
                <div style={s.cardImg} />
                <div style={s.cardBody}>
                  <div
                    style={{
                      height: '16px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      marginBottom: '8px',
                    }}
                  />
                  <div
                    style={{
                      height: '12px',
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: '4px',
                      width: '60%',
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      ) : recipes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255,255,255,0.4)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🍽️</div>
          <p>{t.aucune}</p>
        </div>
      ) : (
        <>
          <div style={s.grid}>
            {recipes.map((r) => (
              <Link
                key={r.id}
                to={`/recipes/${r.id}`}
                style={s.card}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={s.cardImg}>{especeEmoji[r.main_species] || '🍽️'}</div>
                <div style={s.cardBody}>
                  <div style={s.cardMeta}>
                    {r.main_species && (
                      <span style={s.badge('#c9a227')}>
                        {t.especesLabels[r.main_species] || r.main_species}
                      </span>
                    )}
                    {r.difficulty && (
                      <span style={s.badge(DIFFICULTY_COLOR[r.difficulty] || '#aaa')}>
                        {r.difficulty}
                      </span>
                    )}
                    {r.type === 'editoriale' && (
                      <span style={s.badge('#5b8def')}>{t.editorial}</span>
                    )}
                  </div>
                  <div style={s.cardTitle}>{r.title}</div>
                  <div style={s.cardStats}>
                    {r.prep_time_min && (
                      <span>
                        {t.prep} {r.prep_time_min}
                        {t.min}
                      </span>
                    )}
                    {r.cook_time_min && (
                      <span>
                        {t.cuisson} {r.cook_time_min}
                        {t.min}
                      </span>
                    )}
                    {r.reviews_count > 0 && (
                      <span>
                        ⭐ {parseFloat(r.average_rating).toFixed(1)} ({r.reviews_count} {t.avis})
                      </span>
                    )}
                    {r.favorites_count > 0 && <span>♡ {r.favorites_count}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {hasMore && (
            <div style={{ textAlign: 'center', paddingBottom: '3rem' }}>
              <button
                onClick={() => charger(page + 1)}
                style={{
                  padding: '10px 28px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                {loading ? '…' : t.charger}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
